import { Box, Typography, ToggleButtonGroup, ToggleButton, Button } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { makeStyles } from "@mui/styles";
import { CustomInput, CustomSelect } from "../../../components-new/common";
import { useDispatch, useSelector } from "react-redux";
import { setEditStep } from "../../../Redux/TestAuthoring/TestAuthoring";
import { ElementOptions, strategyItems, MobilestrategyItems } from "../constants";
import {
  useResetExecutionData,
  useUpdateTestDataQuery,
  useUpdateMobileLocators,
  useRemoveNLPAction,
} from "../../../useQuery";
import { useEffect, useState, useRef } from "react";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const useStyles = makeStyles(() => ({
  toggleButton: {
    border: "1px solid #D1DBFF !important",
    padding: "12px 14px",
    borderRadius: "50px !important",
    minWidth: "120px",
    textTransform: "capitalize",
    "&.Mui-selected": {
      backgroundColor: "#2550d1",
      color: "#ffffff !important",
      "&:hover": {
        backgroundColor: "#1a3d8f",
      },
    },
  },
  toggleButtonGroup: {
    columnGap: "10px",
    backgroundColor: "#f4f4f4",
    borderRadius: "4px",
    "& .MuiToggleButton-root": {
      color: "#000000",
    },
  },
}));

const hasItemChanged = (original, current) => {
  const relevantFields = [
    'strategy', 'Object_Xpath', 'xpath', 'android_uiautomator', 'id_strategy',
    'element_class', 'element_bounds',
    'xpos', 'ypos', 'samerowtype', 'samerowindex', 'testdataindex',
    'samerowelementtype', 'samerowelementindex', 'iframexpath', 'tabid', 'testdata_source',
    'elementplaceholder', 'classname', 'dataTestId', 'altpath',
    'closesttext', 'closesttoparent', 'closestparenttotarget', 'closesttext_index'
  ];
  
  return relevantFields.some(field => original[field] !== current[field]);
};


const hasMobileLocatorChanged = (original, variables) => {
  const fieldName = getFieldNameFromStrategy(variables.strategy);
  return original[fieldName] !== variables.value;
};

const getFieldNameFromStrategy = (strategy) => {
  switch (strategy) {
    case 'xpath':
      return 'xpath';
    case 'android_uiautomator':
      return 'android_uiautomator';
    case 'id_strategy':
      return 'id_strategy';
    default:
      return strategy;
  }
};

const deriveStrategy = (item) => {
  const validStrategies = [
    "X-Path", "XY", "Search", "SameRow", "SameRowElement",
    "PlaceHolder", "ClassName", "DataTestId", "AttributeXPath",
    "AndroidUIAutomator", "IdStrategy", "ClosestElement",
    "ElementClass", "ElementBounds"
  ];

  if (item.strategy && validStrategies.includes(item.strategy)) return item.strategy;

  const sourceToStrategy = {
    "PlaceHolder": "PlaceHolder",
    "ClassName": "ClassName",
    "DataTestId": "DataTestId",
    "AttributeXPath": "AttributeXPath",
    "ClosestElement": "ClosestElement",
    "SameRowElement": "SameRowElement",
    "SameRow": "SameRow",
    "Search": "Search",
  };

  return sourceToStrategy[item.testdata_source] || "X-Path";
};

const ElementLocator = ({ item, tabs }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const queryClient = useQueryClient();
  const notify = useNotification();
  const isMobile = useSelector((state) => state.authoringSlice.isMobile);
  const originalItemRef = useRef(null);
  const [chooseData, setChooseData] = useState(deriveStrategy(item));

const { mutate: updateTestData } = useUpdateTestDataQuery({
  onSuccess: () => {
    notify("The item has been updated successfully.", "success");
    originalItemRef.current = { ...item };
    queryClient.invalidateQueries(["testCaseResults", "latestRecording"]);
  },
  onError: (error) => {
    notify(error.message || "Failed to update the item", "error");
  },
});

  const { mutate: resetExecution } = useResetExecutionData(item.idtest_step, {
    onSuccess: () => {
      notify("Execution data has been reset successfully.", "success");
    },
    onError: (error) => {
      notify(error.message || "Failed to reset execution data", "error");
    },
  });


const { mutate: removeNLPAction } = useRemoveNLPAction({
  onSuccess: (data) => {
    notify(`${data.message}`, "success");
  },
  onError: (error) => {
    notify(error.message || "Failed to Remove NLP Action", "error");
  }
});

  const selectedModuleID = useSelector((state) => state.authoringSlice.selectedComponent);

const { mutate: updateMobileLocators } = useUpdateMobileLocators({
  onSuccess: (data, variables) => {
    notify("Mobile locators updated successfully", "success");
    const fieldName = getFieldNameFromStrategy(variables.strategy);
    originalItemRef.current = { 
      ...originalItemRef.current, 
      [fieldName]: variables.value 
    };
  },
  onError: (error) => {
    notify(error.message || "Failed to update mobile locators", "error");
  },
});

  const handleChange = (value, field) => {
    dispatch(setEditStep({ ...item, [field]: value }));
  };

  // ====== Updates the Strategy  for both Mob. and Web. =======
const handleSubmit = () => {
  if (!originalItemRef.current || !hasItemChanged(originalItemRef.current, item)) {
    return;
  }
  
  updateTestData({ api_input: item, fromStrategy: true, modid: selectedModuleID });
};

  const handleReset = () => {
    resetExecution();
  };

  const handleRemoveNLPAction = () => {
    removeNLPAction({ tsid: item.idtest_step });
  };

  const handleChangeMultiple = (value) => {
    dispatch(setEditStep({ ...item, ...value }));
  };

  // ====== Update Mobile Locators based on strategy =======
const handleMobileLocatorUpdate = () => {
  let strategy, value;

  switch (chooseData) {
    case "X-Path":
      strategy = "xpath";
      value = item.xpath || "";
      break;
    case "AndroidUIAutomator":
      strategy = "android_uiautomator";
      value = item.android_uiautomator || "";
      break;
    case "IdStrategy":
      strategy = "id_strategy";
      value = item.id_strategy || "";
      break;
    case "ElementClass":
      strategy = "element_class";
      value = item.element_class || "";
      break;
    case "ElementBounds":
      strategy = "element_bounds";
      value = item.element_bounds || "";
      break;
    default:
      notify("Unsupported mobile strategy for locator update", "warning");
      return;
  }

  if (!value.trim()) {
    notify("Please enter a value for the selected strategy", "warning");
    return;
  }

  const variables = { strategy, value };
  if (!originalItemRef.current || !hasMobileLocatorChanged(originalItemRef.current, variables)) {

    return;
  }
  updateMobileLocators({
    strategy: strategy,
    value: value,
    testCaseId: item.test_case_id,
    teststepid: item.idtest_step,
  });
};

useEffect(() => {
  setChooseData(deriveStrategy(item));
}, [item.strategy, item.testdata_source]);

  useEffect(() => {
    handleChangeMultiple({
      strategy: item.strategy,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.strategy]);

  useEffect(() => {
    originalItemRef.current = { ...item };
  }, [item.idtest_step]); 

  const filteredItems = isMobile ? MobilestrategyItems : strategyItems;

  return (
    <Box pr={3} display="flex" flexDirection="column" rowGap={4}>
      <Box>
        <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
          Strategy
        </Typography>
        <ToggleButtonGroup
          className={classes.toggleButtonGroup}
          value={chooseData}
          exclusive
          onChange={(e, value) => {
            const testdataSourceSame = ["Search", "SameRow", "SameRowElement"];
            const testdataSourceRecorded = ["X-Path", "XY", "AndroidUIAutomator", "IdStrategy", "ElementClass", "ElementBounds"];
            const testdataSourceNew = ["PlaceHolder", "ClassName", "DataTestId", "AttributeXPath", "ClosestElement"];
            if (testdataSourceSame.includes(value)) {
              handleChangeMultiple({ strategy: value, testdata_source: value });
            } else if (testdataSourceRecorded.includes(value)) {
              handleChangeMultiple({ strategy: value, testdata_source: "AsRecorded" });
            } else if (testdataSourceNew.includes(value)) { 
              handleChangeMultiple({ strategy: value, testdata_source: value });
            }
          }}
        >
          {filteredItems.map((strItem) => (
            <ToggleButton
              key={strItem.value}
              className={classes.toggleButton}
              value={strItem.value}
            >
              {strItem.label}
              {strItem.value === chooseData && <CheckOutlinedIcon sx={{ ml: 1 }} />}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box display="flex" columnGap={2}>
        {/* Reset button always visible */}
        <Button variant="outlined" color="secondary" onClick={handleReset}>
          Reset Execution
        </Button>

        <Button variant="outlined" color="secondary" onClick={handleRemoveNLPAction}>
          Remove NLP Action
        </Button>
      </Box>

      {(chooseData === "Search" || chooseData === "SameRowElement") && (
        <Box mt={2}>
          <Button
            variant="contained"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>
      )}

      {/* Web X-Path Strategy */}
      {chooseData === "X-Path" && !isMobile && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <CustomInput
            labelinput={"Object Identifier"}
            onChange={(e) => handleChange(e.target.value, "Object_Xpath")}
            placeholder={"Enter Object Identifier"}
            value={item.Object_Xpath}
            flex={1}
          />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* Mobile X-Path Strategy */}
      {chooseData === "X-Path" && isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Object Identifier"}
              onChange={(e) => handleChange(e.target.value, "xpath")}
              placeholder={"Enter Object Identifier"}
              value={item.xpath}
              flex={1}
            />
            <Box mt={2} display="flex" flexDirection="column" rowGap={1}>
              <Button variant="contained" onClick={handleSubmit}>
                {/*Update Strategy*/}
                Update
              </Button>
              <Button
                variant="outlined"
                onClick={handleMobileLocatorUpdate}
                disabled={!item.xpath?.trim()}
              >
                {/*Update Locator*/}
                Update Locator
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile AndroidUIAutomator Strategy */}
      {chooseData === "AndroidUIAutomator" && isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Android UI Automator"}
              onChange={(e) => handleChange(e.target.value, "android_uiautomator")}
              placeholder={"Enter Android UI Automator"}
              value={item.android_uiautomator}
              flex={1}
            />
            <Box mt={2} display="flex" flexDirection="column" rowGap={1}>
              <Button variant="contained" onClick={handleSubmit}>
                Update Strategy
              </Button>
              <Button
                variant="outlined"
                onClick={handleMobileLocatorUpdate}
                disabled={!item.android_uiautomator?.trim()}
              >
                Update Locator
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile IdStrategy */}
      {chooseData === "IdStrategy" && isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Id Strategy"}
              onChange={(e) => handleChange(e.target.value, "id_strategy")}
              placeholder={"Enter Id Strategy"}
              value={item.id_strategy}
              flex={1}
            />
            <Box mt={2} display="flex" flexDirection="column" rowGap={1}>
              <Button variant="contained" onClick={handleSubmit}>
                Update Strategy
              </Button>
              <Button
                variant="outlined"
                onClick={handleMobileLocatorUpdate}
                disabled={!item.id_strategy?.trim()}
              >
                Update Locator
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile Element Class */}
      {chooseData === "ElementClass" && isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Element Class"}
              onChange={(e) => handleChange(e.target.value, "element_class")}
              placeholder={"Enter Element Class"}
              value={item.element_class}
              flex={1}
            />
            <Box mt={2} display="flex" flexDirection="column" rowGap={1}>
              <Button variant="contained" onClick={handleSubmit}>
                Update Strategy
              </Button>
              <Button
                variant="outlined"
                onClick={handleMobileLocatorUpdate}
                disabled={!item.element_class?.trim()}
              >
                Update Locator
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile Element Bounds */}
      {chooseData === "ElementBounds" && isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Element Bounds"}
              onChange={(e) => handleChange(e.target.value, "element_bounds")}
              placeholder={"Enter Element Bounds"}
              value={item.element_bounds}
              flex={1}
            />
            <Box mt={2} display="flex" flexDirection="column" rowGap={1}>
              <Button variant="contained" onClick={handleSubmit}>
                Update Strategy
              </Button>
              <Button
                variant="outlined"
                onClick={handleMobileLocatorUpdate}
                disabled={!item.element_bounds?.trim()}
              >
                Update Locator
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Web XY Strategy */}
      {chooseData === "XY" && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <CustomInput
            labelinput="X Coordinate"
            onChange={(e) => handleChange(e.target.value, "xpos")}
            placeholder={"Enter X Coordinate"}
            value={item.xpos}
            type="number"
          />
          <CustomInput
            labelinput="Y Coordinate"
            onChange={(e) => handleChange(e.target.value, "ypos")}
            placeholder={"Enter Y Coordinate"}
            value={item.ypos}
            type="number"
          />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {chooseData === "SameRow" && (
        <Box display="flex" flexDirection="column" rowGap={3}>
          <Box display="flex" columnGap={4}>
            {/* Element Details Section */}
            <Box flex={1}>
              <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                Element Details
              </Typography>
              <Box display="flex" columnGap={2} alignItems="center" mt={1}>
                <CustomInput
                  labelinput={"Element Type"}
                  placeholder={"Enter element type"}
                  value={item.samerowtype}
                  onChange={(e) => handleChange(e.target.value, "samerowtype")}
                  width={"100%"}
                />
                <CustomInput
                  labelinput={"Element Index"}
                  placeholder={"Enter element index"}
                  value={item.samerowindex}
                  onChange={(e) => handleChange(e.target.value, "samerowindex")}
                />
              </Box>
            </Box>

            <Box flex={1}>
              <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                Test Data Details
              </Typography>
              <Box display="flex" columnGap={2} alignItems="center" mt={1}>
                <CustomInput
                  labelinput={"Test Data Index"}
                  placeholder={"Enter test data index"}
                  value={item?.testdataindex || ""}
                  onChange={(e) => handleChange(e.target.value, "testdataindex")}
                />
              </Box>
            </Box>
          </Box>
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {chooseData === "SameRowElement" && (
        <Box display="flex" flexDirection="column" rowGap={3}>
          <Box display="flex" columnGap={4}>
            {/* Element Details Section */}
            <Box flex={1}>
              <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                Element Details
              </Typography>
              <Box display="flex" columnGap={2} alignItems="center" mt={1}>
                <CustomInput
                  labelinput={"Element Type"}
                  placeholder={"Enter element type"}
                  value={item.samerowelementtype}
                  onChange={(e) => handleChange(e.target.value, "samerowelementtype")}
                  width={"100%"}
                />
                <CustomInput
                  labelinput={"Element Index"}
                  placeholder={"Enter element index"}
                  value={item.samerowelementindex}
                  onChange={(e) => handleChange(e.target.value, "samerowelementindex")}
                />
              </Box>
            </Box>
          </Box>
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}


      {/* PlaceHolder Strategy */}
      {chooseData === "PlaceHolder" && !isMobile && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <CustomInput
            labelinput={"PlaceHolder"}
            onChange={(e) => handleChange(e.target.value, "elementplaceholder")}
            placeholder={"Enter PlaceHolder"}
            value={item.elementplaceholder}
            flex={1}
          />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* ClassName Strategy */}
      {chooseData === "ClassName" && !isMobile && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <CustomInput
            labelinput={"ClassName"}
            onChange={(e) => handleChange(e.target.value, "classname")}
            placeholder={"Enter ClassName"}
            value={item.classname}
            flex={1}
          />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* Data-Test-Id Strategy */}
{chooseData === "DataTestId" && !isMobile && (
  <Box display="flex" columnGap={2} alignItems={"center"}>
    <CustomInput
      labelinput={"Data-Test-Id"}
      onChange={(e) => handleChange(e.target.value, "dataTestId")}
      placeholder={"Enter Data-Test-Id"}
      value={item.dataTestId}
      flex={1}
    />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* Attribute XPath Strategy */}
      {chooseData === "AttributeXPath" && !isMobile && (
        <Box display="flex" columnGap={2} alignItems={"center"}>
          <CustomInput
            labelinput={"Attribute XPath"}
            onChange={(e) => handleChange(e.target.value, "altpath")}
            placeholder={"Enter Attribute XPath"}
            value={item.altpath}
            flex={1}
          />
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}

     {/* Closest Element Strategy */}
      {chooseData === "ClosestElement" && !isMobile && (
        <Box display="flex" flexDirection="column" rowGap={2}>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Closest Text"}
              onChange={(e) => handleChange(e.target.value, "closesttext")}
              placeholder={"Enter Closest Text"}
              value={item.closesttext}
              flex={1}
            />
            <CustomInput
              labelinput={"Closest To Parent"}
              onChange={(e) => handleChange(e.target.value, "closesttoparent")}
              placeholder={"Enter Closest To Parent"}
              value={item.closesttoparent}
              flex={1}
            />
          </Box>
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <CustomInput
              labelinput={"Closest Parent To Target"}
              onChange={(e) => handleChange(e.target.value, "closestparenttotarget")}
              placeholder={"Enter Closest Parent To Target"}
              value={item.closestparenttotarget}
              flex={1}
            />
            <CustomInput
              labelinput={"Closest Text Index"}
              onChange={(e) => handleChange(e.target.value, "closesttext_index")}
              placeholder={"Enter Closest Text Index"}
              value={item.closesttext_index}
              flex={1}
            />
          </Box>
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )} 

      {/* Web-only fields */}
      {!isMobile && (
        <>
          <CustomInput
            labelinput={"Iframexpath"}
            placeholder={"Enter Iframexpath"}
            value={item.iframexpath}
            onChange={(e) => handleChange(e.target.value, "iframexpath")}
          />
          <CustomSelect
            options={tabs.map((tab) => ({
              label: tab.tabName,
              value: tab.tabId,
            }))}
            value={item.tabid || ""}
            label={"Tabs"}
            onChange={(e) => handleChange(e.target.value, "tabid")}
          />
        </>
      )}
    </Box>
  );
};

export default ElementLocator;
